import { QueryClient } from "@tanstack/react-query"

import { announcements } from "./announcements"
import { auth } from "./auth"
import { boards } from "./boards"
import { challenges } from "./challenges"
import { connections } from "./connections"
import { groups } from "./groups"
import { ideas } from "./ideas"
import { innointel2 } from "./innointel2"
import { innovationIntelligence } from "./innovationIntelligence"
import { invitations } from "./invitations"
import { journey } from "./journey"
import { messages } from "./messages"
import { notifications } from "./notifications"
import { organisations } from "./organisations"
import { pages } from "./pages"
import { persistentTokens } from "./persistentTokens"
import { polls } from "./polls"
import { roles } from "./roles"
import { search } from "./search"
import { superadmin } from "./superadmin"
import { tags } from "./tags"
import { uploads } from "./uploads"
import { users } from "./users"
import { verifications } from "./verifications"
import { webhooks } from "./webhooks"

// Type helper that extract the parameters of an API function since they are always structured [...parameters, successCallback, failCallback]
// Therefore this extrats those last two callbacks which is useful to not only destructure the params, but also gives us access to the success callback return type
type APIParams<T extends (...args: any[]) => any> = T extends (...args: infer P) => any
  ? P extends [...infer Params, infer SuccessCallback, infer FailCallback]
    ? [Params, SuccessCallback, FailCallback]
    : never
  : never
// Type helper that extracts the return type of the success callback of any API function
export type Response<T extends (...args: any[]) => any> = Parameters<APIParams<T>[1]>[0]

// asQuery can be used for easier compatibility between the simplydo codebase and the react-query library, for the most part this just transforms the callback pattern to a promise. Under the hood we also make some typescript inference to make sure useQuery can infer the correct parameters and return type of the queryFn
// Generally just wrapping any API function with asQuery will make it compatible with react-query's useQuery hook as the value for the queryFn
// This will only work for api functions that end with the typical success and fail callback pattern (which should be all of them!)
export const asQuery = <
  T extends (...args: any[]) => any,
  APIParam extends APIParams<T>,
  TBody extends Parameters<APIParam[1]>[0],
>(
  fn: T,
  ...params: APIParam[0] extends { 0: any }
    ? [{ params: APIParam[0]; onSuccess?: () => void; onError?: () => void }]
    : [] | [{ params?: []; onSuccess?: () => void; onError?: () => void }]
): (<T extends TBody>() => Promise<T>) => {
  return async () =>
    await new Promise((resolve, reject) => {
      fn(
        ...(params[0]?.params ?? []),
        (response) => {
          params[0]?.onSuccess?.()
          resolve(response)
        },
        (error) => {
          params[0]?.onError?.()
          reject(error)
        },
      )
    })
}

// This is conceptually the same as asQuery, but for mutations instead of queries
// Instead of having to supply parameters directly to the definition, you supply them to the .mutate function call later.
type MutationFunction<TData, TVariables> = (variables: TVariables) => Promise<TData>
export const asMutation = <
  T extends (...args: any[]) => any,
  APIParam extends APIParams<T>,
  TBody extends Parameters<APIParam[1]>[0],
  TParams extends APIParam[0] extends { 0: any } ? { params: APIParam[0] } : never,
>(
  fn: T,
): MutationFunction<TBody, TParams> => {
  return async (vars: TParams) =>
    await new Promise((resolve, reject) => {
      fn(
        ...(vars?.params ?? []),
        (response: TBody) => {
          resolve(response)
        },
        (error) => {
          reject(error)
        },
      )
    })
}

type APIOptions = {
  getApiUrl: () => string
  onLogin: (success: () => void, fail: () => void) => void
  onLogout?: () => void
  onBeforeRequest?: (xhr: XMLHttpRequest) => void
  onFailedRequest?: (xhr: XMLHttpRequest, onFail: (error: any) => void) => void
}

export class API {
  token: string | null
  revertToken: string | null
  webVersion: string | null
  queryClient: QueryClient
  options: APIOptions

  auth: ReturnType<typeof auth>
  search: ReturnType<typeof search>
  superadmin: ReturnType<typeof superadmin>
  challenges: ReturnType<typeof challenges>
  ideas: ReturnType<typeof ideas>
  groups: ReturnType<typeof groups>
  uploads: ReturnType<typeof uploads>
  users: ReturnType<typeof users>
  organisations: ReturnType<typeof organisations>
  messages: ReturnType<typeof messages>
  notifications: ReturnType<typeof notifications>
  polls: ReturnType<typeof polls>
  tags: ReturnType<typeof tags>
  webhooks: ReturnType<typeof webhooks>
  announcements: ReturnType<typeof announcements>
  invitations: ReturnType<typeof invitations>
  verifications: ReturnType<typeof verifications>
  boards: ReturnType<typeof boards>
  innovationIntelligence: ReturnType<typeof innovationIntelligence>
  innointel2: ReturnType<typeof innointel2>
  journey: ReturnType<typeof journey>
  roles: ReturnType<typeof roles>
  persistentTokens: ReturnType<typeof persistentTokens>
  connections: ReturnType<typeof connections>
  pages: ReturnType<typeof pages>

  constructor(options: APIOptions) {
    this.token = null
    this.revertToken = null
    this.webVersion = null
    this.queryClient = new QueryClient({
      defaultOptions: {
        queries: {
          retry: false,
          staleTime: 60000,
          refetchOnMount: false,
          refetchOnWindowFocus: false,
          refetchOnReconnect: false,
        },
      },
    })
    this.options = options

    this.journey = journey(this)
    this.auth = auth(this)
    this.search = search(this)
    this.superadmin = superadmin(this)
    this.challenges = challenges(this)
    this.ideas = ideas(this)
    this.groups = groups(this)
    this.uploads = uploads(this)
    this.users = users(this)
    this.organisations = organisations(this)
    this.messages = messages(this)
    this.notifications = notifications(this)
    this.polls = polls(this)
    this.tags = tags(this)
    this.webhooks = webhooks(this)
    this.announcements = announcements(this)
    this.invitations = invitations(this)
    this.verifications = verifications(this)
    this.boards = boards(this)
    this.innovationIntelligence = innovationIntelligence(this)
    this.innointel2 = innointel2(this)
    this.roles = roles(this)
    this.persistentTokens = persistentTokens(this)
    this.connections = connections(this)
    this.pages = pages(this)
  }

  TOKEN_KEY = "auth_token"
  REVERT_TOKEN = "revert_token"

  onFailedRequest(xhr, onFail) {
    if (this.options.onFailedRequest) {
      this.options.onFailedRequest(xhr, onFail)
      return
    }
    if (xhr.status === 530) {
      onFail?.({
        status: xhr.status,
        message: "Access from this IP address is not allowed",
      })
      return
    }
    try {
      const response = JSON.parse(xhr.responseText)
      onFail?.({
        status: xhr.status,
        message: response.message || xhr.statusText,
      })
    } catch {
      onFail?.({
        status: xhr.status,
        message: xhr.statusText,
      })
    }
  }

  req(method, path, data, success, fail, withAuth) {
    const xhr = new XMLHttpRequest()

    const url = this.options.getApiUrl()

    xhr.open(method, `${url}${path}`)
    xhr.setRequestHeader("Content-Type", "application/json")
    if (withAuth && this.token) {
      xhr.setRequestHeader("Authorization", `Bearer ${this.token}`)
    }
    xhr.setRequestHeader("SimplyDo-Device", navigator.userAgent)

    this.options?.onBeforeRequest?.(xhr)

    xhr.onreadystatechange = () => {
      if (xhr.readyState !== 4) {
        return
      }
      if (xhr.status !== 200) {
        this.onFailedRequest?.(xhr, fail)
        return
      }

      this.webVersion = xhr.getResponseHeader("SimplyDo-WebVersion")

      try {
        const response = JSON.parse(xhr.responseText)
        success?.(response)
      } catch (err) {
        console.error(err)

        fail?.({
          status: 400,
          message: "There was a problem with this request",
        })
      }
    }
    xhr.send(data && JSON.stringify(data))
  }

  unauthenticatedRequest(method, path, data, success, fail) {
    this.req(method, path, data, success, fail, false)
  }

  authenticatedRequest(method, path, data, success, fail) {
    if (!this.token) return
    this.req(method, path, data, success, fail, true)
  }

  maybeAuthenticatedRequest(method, path, data, success, fail) {
    if (this.token) {
      this.authenticatedRequest(method, path, data, success, fail)
    } else {
      this.unauthenticatedRequest(method, path, data, success, fail)
    }
  }
}
